import restClient from "../restClient";

const SITES_BASE_DOMAIN = process.env.REACT_APP_SITES_BASE_DOMAIN || "dealertiq.com";

const pickDealerSite = (payload) => {
  if (!payload) return null;
  if (payload.results?.length) return payload.results[0];
  if (Array.isArray(payload.data) && payload.data.length) return payload.data[0];
  if (payload.id || payload.documentId) return payload;
  return null;
};

const normalizeHost = (value) =>
  String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/$/, "");

/** Public dealer-site inventory host: custom domain or `{subdomain}.{SITES_BASE_DOMAIN}`. */
export const resolveDealerSitePublicHost = (dealerSite) => {
  const custom = normalizeHost(dealerSite?.custom_domain || dealerSite?.customDomain);
  if (custom) return custom;
  const subdomain = String(dealerSite?.subdomain || "").trim();
  if (subdomain) return `${subdomain}.${SITES_BASE_DOMAIN}`;
  return "";
};

const unwrapPublicInventory = (payload) => {
  if (!payload) return null;
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    const nested = payload.data;
    if (
      nested.documentId ||
      nested.id ||
      nested.inventory_vehicle ||
      nested.stock_number ||
      nested.vin
    ) {
      return nested;
    }
  }
  return payload;
};

export const getPublicDealerSiteInventory = async (documentId, host) => {
  const response = await restClient.get(
    `dealer-sites/public/inventory/${encodeURIComponent(documentId)}`,
    {
      params: { host: normalizeHost(host) },
    }
  );
  return unwrapPublicInventory(response.data);
};

export const getDealerSites = async () => {
  const token = localStorage.getItem("token");
  const response = await restClient.get("dealer-sites", {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return pickDealerSite(response.data);
};

export const updateDealerSite = async (id, data) => {
  const token = localStorage.getItem("token");
  const response = await restClient.put(
    `dealer-sites/${id}`,
    { data },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return pickDealerSite(response.data) || response.data;
};

export const updateDealerSiteConfig = async (id, config) =>
  updateDealerSite(id, { config });

export const buildDealerSiteSettingsPayload = (settings = {}) => {
  const payload = {
    custom_domain: settings.customDomain?.trim() || "",
    inventory_publish_after_days: Number(settings.inventoryPublishAfterDays) || 0,
  };

  if (settings.faviconId) {
    payload.favicon = settings.faviconId;
  } else if (settings.faviconUrl) {
    payload.favicon_url = settings.faviconUrl;
  } else {
    payload.favicon = null;
  }

  return payload;
};

export const updateDealerSiteSettings = async (id, settings) =>
  updateDealerSite(id, buildDealerSiteSettingsPayload(settings));

/**
 * Persist the Web Maker config and editable dealer-site settings in one
 * authenticated dealer-site update. The backend validates expected_updated_at
 * immediately before the record update so a stale editor cannot overwrite a
 * newer tab/session.
 */
export const updateDealerSiteEditorState = async (
  id,
  { config, settings, expectedUpdatedAt } = {}
) => {
  const payload = {
    ...(config !== undefined ? { config } : {}),
    ...(settings !== undefined ? buildDealerSiteSettingsPayload(settings) : {}),
    ...(expectedUpdatedAt ? { expected_updated_at: expectedUpdatedAt } : {}),
  };

  return updateDealerSite(id, payload);
};
